import { useState } from "react";
import { ChevronDown, Target, Users } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export const ProblemCard = ({ problem, verticalName, index }) => {
  const [expanded, setExpanded] = useState(false);
  const rankNum = (index ?? 0) + 1;

  return (
    <div data-testid={`problem-card-${problem.id}`} className="group">
      <div className="py-6 md:py-8">
        <div className="flex gap-3 md:gap-6 items-start">
          {/* Rank Number */}
          <div className="flex-shrink-0 w-8 md:w-14 pt-1">
            <span className="text-2xl md:text-4xl font-bold text-slate-200 group-hover:text-[#0066CC]/30 transition-colors">
              {rankNum}
            </span>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="text-base sm:text-xl md:text-2xl font-bold text-slate-900 group-hover:text-[#0066CC] transition-colors leading-tight">
              {problem.title}
            </h3>

            {problem.challenge && (
              <p className="text-xs sm:text-sm text-slate-500 mt-1 line-clamp-2 sm:line-clamp-1">
                {problem.challenge}
              </p>
            )}

            {/* Toggle Details */}
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1.5 mt-3 md:mt-5 text-xs sm:text-sm font-medium text-[#0066CC] hover:text-[#0052A3] transition-colors"
              data-testid={`problem-expand-${problem.id}`}
            >
              <span>{expanded ? "Hide details" : "What's this challenge about?"}</span>
              <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
            </button>

            {/* Expandable Content */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeInOut" }}
                  className="overflow-hidden"
                >
                  <div className="pt-3 md:pt-4 space-y-3 md:space-y-4">
                    {problem.description && (
                      <div>
                        <h4 className="text-xs sm:text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                          <Target className="w-4 h-4 text-[#0066CC]" />
                          The Challenge
                        </h4>
                        <p className="text-slate-600 text-xs sm:text-sm leading-relaxed pl-6">
                          {problem.description}
                        </p>
                      </div>
                    )}

                    <div>
                      <h4 className="text-xs sm:text-sm font-semibold text-slate-900 mb-1.5 flex items-center gap-2">
                        <Users className="w-4 h-4 text-[#0066CC]" />
                        Who should explore this solution?
                      </h4>
                      <ul className="text-slate-600 text-xs sm:text-sm leading-relaxed pl-6 space-y-1">
                        {problem.target_audience && problem.target_audience.length > 0 ? (
                          problem.target_audience.map((item, i) => (
                            <li key={i} className="flex items-start gap-2">
                              <span className="text-[#0066CC] mt-1.5 text-[6px]">&#9679;</span>
                              <span>{item}</span>
                            </li>
                          ))
                        ) : (
                          <>
                            <li className="flex items-start gap-2">
                              <span className="text-[#0066CC] mt-1.5 text-[6px]">&#9679;</span>
                              <span>{verticalName} businesses facing operational inefficiencies</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[#0066CC] mt-1.5 text-[6px]">&#9679;</span>
                              <span>Operations managers seeking data-driven decision making</span>
                            </li>
                            <li className="flex items-start gap-2">
                              <span className="text-[#0066CC] mt-1.5 text-[6px]">&#9679;</span>
                              <span>Business owners looking for measurable ROI improvements</span>
                            </li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="border-b border-slate-100" />
    </div>
  );
};
